import { useCallback, useSyncExternalStore } from "react";
import type {
  Category,
  EventId,
  EventSubtask,
  PlanEvent,
  RecurrenceRule,
} from "../../../shared/types";
import {
  subscribe,
  getSnapshot,
  importEvents as storeImportEvents,
  undoEvent as storeUndoEvent,
  redoEvent as storeRedoEvent,
  undoStack,
  redoStack,
} from "../store/event-store";
import {
  addEvent as storeAddEvent,
  deleteEvent as storeDeleteEvent,
  deleteEventLocal as storeDeleteEventLocal,
  confirmDeleteEvent as storeConfirmDeleteEvent,
  updateEvent as storeUpdateEvent,
  detachOccurrence as storeDetachOccurrence,
  addRecurrenceException as storeAddRecurrenceException,
  resyncOccurrence as storeResyncOccurrence,
  resyncAllOccurrences as storeResyncAllOccurrences,
  addEventFull as storeAddEventFull,
} from "../store/event-mutations";
import { generateRecurringInstances } from "../utils/event-helpers";

export function useEvents() {
  const events = useSyncExternalStore(subscribe, getSnapshot);

  return {
    events,
    addEvent: useCallback(
      (
        title: string,
        category: Category,
        date: string,
        startTime: string,
        endTime: string,
      ) => storeAddEvent(title, category, date, startTime, endTime),
      [],
    ),
    addEventFull: useCallback(
      (data: {
        title: string;
        category: Category;
        date: string;
        startTime: string;
        endTime: string;
        allDay?: boolean;
        description: string;
        notes: string;
        tags: readonly string[];
        recurrence: RecurrenceRule | null;
        reminderMinutes: number | null;
        reminderTime: string | null;
        reminderType: "BEFORE" | "AT_TIME" | null;
        eventSubtasks?: readonly EventSubtask[];
      }) => storeAddEventFull(data),
      [],
    ),
    deleteEvent: useCallback((id: EventId) => storeDeleteEvent(id), []),
    deleteEventLocal: useCallback((id: EventId) => storeDeleteEventLocal(id), []),
    confirmDeleteEvent: useCallback((id: EventId) => storeConfirmDeleteEvent(id), []),
    importEvents: useCallback((data: unknown[]) => storeImportEvents(data), []),
    detachOccurrence: useCallback(
      (
        baseId: EventId,
        originalDate: string,
        data: Parameters<typeof storeDetachOccurrence>[2],
      ) => storeDetachOccurrence(baseId, originalDate, data),
      [],
    ),
    addRecurrenceException: useCallback(
      (baseId: EventId, date: string) =>
        storeAddRecurrenceException(baseId, date),
      [],
    ),
    resyncOccurrence: useCallback(
      (baseId: EventId, date: string, detachedId: EventId) =>
        storeResyncOccurrence(baseId, date, detachedId),
      [],
    ),
    resyncAllOccurrences: useCallback(
      (baseId: EventId) => storeResyncAllOccurrences(baseId),
      [],
    ),
    undoEvent: useCallback(() => storeUndoEvent(), []),
    redoEvent: useCallback(() => storeRedoEvent(), []),
    canUndoEvent: undoStack.length > 0,
    canRedoEvent: redoStack.length > 0,
    updateEvent: useCallback(
      (
        id: EventId,
        updates: Partial<
          Pick<
            PlanEvent,
            | "title"
            | "date"
            | "startTime"
            | "endTime"
            | "description"
            | "notes"
            | "tags"
            | "recurrence"
            | "reminderMinutes"
            | "category"
            | "eventSubtasks"
          >
        >,
      ) => storeUpdateEvent(id, updates),
      [],
    ),
    getByDate: useCallback(
      (date: string) => events.filter((e) => e.date === date),
      [events],
    ),
    getRecurringInRange: useCallback(
      (startDate: string, endDate: string) =>
        generateRecurringInstances(events, startDate, endDate),
      [events],
    ),
  };
}
